import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    this.removeAttribute('video-playing');

    videoElement.addEventListener('playing', () => {
      this.setAttribute('video-playing', '');
    }, { once: true });

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.playsInline = true;
    videoElement.controls = false;
    videoElement.removeAttribute('controls');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');

    const startPlayback = () => {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;

        if (videoSource) source.setAttribute('src', videoSource);
      }

      videoElement.load();

      const playPromise = videoElement.play();

      if (playPromise) {
        playPromise.catch(() => {
          // Autoplay can still be blocked by browser settings; keep the background non-interactive.
          this.removeAttribute('video-playing');
        });
      }
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.disconnect();
            startPlayback();
          }
        }
      }, { rootMargin: '200px' });

      observer.observe(this);
    } else {
      startPlayback();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
